import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useAtom } from 'jotai';
import {
  countGroupedSessions,
  flatten,
  project,
  type SessionListQuery,
  type SessionRef,
  type SessionWorkItem,
} from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { getSDK } from '../../services/sdk-instance';
import {
  sessionCollapsedGroupsAtom,
  sessionGoalFilterAtom,
  sessionGroupingAtom,
  sessionRoleFilterAtom,
  sessionShowEmptyGroupsAtom,
  sessionSortAtom,
  sessionStatusFilterAtom,
  sessionWorkspaceFilterAtom,
} from '../../stores/chat/sidebar';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import {
  useClearOppositeSessionList,
  useDeleteManySessions,
  useSessionListBrowse,
  useSessionListSearch,
} from '../../hooks/useSessionList';
import { SessionListToolbar } from './SessionListToolbar';
import { SessionListBody } from './SessionListBody';

export function ChatSessionSidebar() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const startNewChat = useCallback(() => {
    void getSDK().chat.startNewChat();
  }, []);
  const deleteMany = useDeleteManySessions();

  const [grouping, setGrouping] = useAtom(sessionGroupingAtom);
  const [roleFilter, setRoleFilter] = useAtom(sessionRoleFilterAtom);
  const [goalFilter, setGoalFilter] = useAtom(sessionGoalFilterAtom);
  const [statusFilter, setStatusFilter] = useAtom(sessionStatusFilterAtom);
  const [workspaceFilter, setWorkspaceFilter] = useAtom(sessionWorkspaceFilterAtom);
  const [sort, setSort] = useAtom(sessionSortAtom);
  const [showEmptyGroups, setShowEmptyGroups] = useAtom(sessionShowEmptyGroupsAtom);
  const [collapsedList, setCollapsedList] = useAtom(sessionCollapsedGroupsAtom);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Map<string, SessionRef>>(new Map());

  const exitSelecting = useCallback(() => {
    setSelecting(false);
    setSelected(new Map());
  }, []);

  const trimmedQuery = searchQuery.trim();
  const mode = trimmedQuery === '' ? 'browse' : 'search';
  useClearOppositeSessionList(mode);

  const query: SessionListQuery = useMemo(
    () => ({
      grouping,
      roleFilter,
      goalFilter,
      statusFilter,
      workspaceFilter,
      sort,
      showEmptyGroups,
    }),
    [grouping, roleFilter, goalFilter, statusFilter, workspaceFilter, sort, showEmptyGroups],
  );

  const browse = useSessionListBrowse(query, mode === 'browse');
  const search = useSessionListSearch(trimmedQuery, mode === 'search');

  const browseSessions = useMemo(
    () => browse.data?.pages.flatMap((page) => page.sessions) ?? [],
    [browse.data],
  );
  const browseWorkItems = useMemo(
    () => browse.data?.pages.flatMap((page) => page.workItems) ?? [],
    [browse.data],
  );
  const catalogs = browse.data?.pages.find((page) => page.catalogs)?.catalogs;

  const view = useMemo(() => {
    if (mode !== 'browse') return null;
    return project(browseSessions, browseWorkItems, query, catalogs);
  }, [mode, browseSessions, browseWorkItems, query, catalogs]);

  const collapsedKeys = useMemo(() => new Set(collapsedList), [collapsedList]);
  const rows = useMemo(() => (view ? flatten(view, collapsedKeys) : []), [view, collapsedKeys]);

  const workItemsBySession = useMemo(() => {
    const map = new Map<string, SessionWorkItem[]>();
    for (const item of browseWorkItems) {
      const existing = map.get(item.session_id);
      if (existing) existing.push(item);
      else map.set(item.session_id, [item]);
    }
    return map;
  }, [browseWorkItems]);

  const searchHits = useMemo(() => {
    const raw = search.data?.pages.flatMap((page) => page.results) ?? [];
    return raw.filter((hit) => hit.entity_type === 'session');
  }, [search.data]);

  const { data: workspaces } = useWorkspaces();
  const workspaceOptions = useMemo(
    () =>
      (workspaces ?? []).map((workspace) => ({
        id: workspace.workspace_id,
        label: workspace.name,
      })),
    [workspaces],
  );

  const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);

  const toggleCollapsed = useCallback((key: string) => {
    setCollapsedList((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  }, [setCollapsedList]);

  const toggleSelect = useCallback((ref: SessionRef) => {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(ref.session_id)) next.delete(ref.session_id);
      else next.set(ref.session_id, ref);
      return next;
    });
  }, []);

  const selectGroup = useCallback((refs: SessionRef[]) => {
    setSelected((current) => {
      const next = new Map(current);
      const allSelected = refs.every((ref) => next.has(ref.session_id));
      for (const ref of refs) {
        if (allSelected) next.delete(ref.session_id);
        else next.set(ref.session_id, ref);
      }
      return next;
    });
  }, []);

  // Session switches commit the drop and the route in one flushSync (app-shell ADR-026).
  const handleNewChat = useCallback(() => {
    flushSync(() => {
      startNewChat();
      void navigate('/chat');
    });
  }, [navigate, startNewChat]);

  const handleOpen = useCallback(
    (id: string) => {
      void navigate(`/chat/${id}`, { flushSync: true });
    },
    [navigate],
  );

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const handleDeleteRow = useCallback(
    (id: string, workspaceId: string | null | undefined, e: React.MouseEvent) => {
      e.stopPropagation();
      setPendingDelete({ kind: 'row', items: [{ session_id: id, workspace_id: workspaceId ?? null }] });
    },
    [],
  );

  const handleBulkDelete = useCallback(() => {
    const items = Array.from(selected.values());
    if (items.length === 0) return;
    setPendingDelete({ kind: 'bulk', items });
  }, [selected]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const { kind, items } = pendingDelete;
    setPendingDelete(null);
    // Leave the open session through the switch seam before deleting it (app-shell ADR-026).
    if (sessionId && items.some((item) => item.session_id === sessionId)) handleNewChat();
    const result = await deleteMany.mutateAsync(items);
    if (result.failed.length > 0) {
      throw new Error(
        kind === 'row'
          ? `Delete failed for session ${items[0].session_id}: ${result.failed[0].error}`
          : `Bulk delete failed for ${result.failed.length} session${result.failed.length === 1 ? '' : 's'}`,
      );
    }
    if (kind === 'bulk') setSelected(new Map());
  }, [deleteMany, handleNewChat, pendingDelete, sessionId]);

  const browseError = errorWithNothingCachedToShow(browse.data, browse.error);
  const searchError = errorWithNothingCachedToShow(search.data, search.error);
  const listError = mode === 'browse' ? Boolean(browseError) : Boolean(searchError);
  const isEmpty = mode === 'browse' && browse.isSuccess && browseSessions.length === 0;
  const noMatches =
    mode === 'browse' &&
    !listError &&
    browseSessions.length > 0 &&
    countGroupedSessions(view?.groups ?? []) === 0;

  const loading = mode === 'browse' ? browse.isLoading : search.isLoading;
  const loadingMore = mode === 'browse' ? browse.isFetchingNextPage : search.isFetchingNextPage;
  const hasMore = Boolean(mode === 'browse' ? browse.hasNextPage : search.hasNextPage);
  const onLoadMore = useCallback(() => {
    if (mode === 'browse') void browse.fetchNextPage();
    else void search.fetchNextPage();
  }, [mode, browse, search]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SessionListToolbar
        grouping={grouping}
        onGroupingChange={setGrouping}
        roleFilter={roleFilter}
        roleOptions={view?.roleOptions ?? []}
        onRoleFilterChange={setRoleFilter}
        goalFilter={goalFilter}
        goalOptions={view?.goalOptions ?? []}
        onGoalFilterChange={setGoalFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        workspaceFilter={workspaceFilter}
        workspaceOptions={workspaceOptions}
        onWorkspaceFilterChange={setWorkspaceFilter}
        sort={sort}
        onSortChange={setSort}
        showEmptyGroups={showEmptyGroups}
        onShowEmptyGroupsChange={setShowEmptyGroups}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchOpen={searchOpen || trimmedQuery !== ''}
        onSearchOpenChange={(open) => {
          setSearchOpen(open);
          if (!open) setSearchQuery('');
        }}
        onNewChat={handleNewChat}
        selecting={selecting}
        onSelectingChange={(next) => {
          if (next) setSelecting(true);
          else exitSelecting();
        }}
      />

      {selecting && (
        <div
          className="flex items-center justify-between border-b px-3 py-1.5 text-xs"
          data-testid="session-list-selection-bar"
        >
          <span>{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-destructive"
              data-testid="session-list-bulk-delete"
              disabled={deleteMany.isPending || selected.size === 0}
              onClick={handleBulkDelete}
            >
              Delete
            </button>
            <button
              type="button"
              data-testid="session-list-bulk-done"
              onClick={exitSelecting}
            >
              Done
            </button>
          </div>
        </div>
      )}

      <SessionListBody
        mode={mode}
        rows={rows}
        searchHits={searchHits}
        view={view}
        workItemsBySession={workItemsBySession}
        collapsedKeys={collapsedKeys}
        selectedIds={selectedIds}
        selecting={selecting}
        activeSessionId={sessionId}
        deleting={deleteMany.isPending}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        listError={listError}
        isEmpty={isEmpty}
        noMatches={noMatches}
        onToggleCollapsed={toggleCollapsed}
        onToggleSelect={toggleSelect}
        onSelectGroup={selectGroup}
        onOpenSession={handleOpen}
        onDeleteRow={handleDeleteRow}
        onLoadMore={onLoadMore}
      />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <DialogContent data-testid="session-delete-dialog">
          <DialogHeader>
            <DialogTitle>
              {pendingDelete?.kind === 'bulk'
                ? `Delete ${pendingDelete.items.length} loaded session${pendingDelete.items.length === 1 ? '' : 's'}?`
                : 'Delete this session?'}
            </DialogTitle>
            <DialogDescription>
              {pendingDelete?.kind === 'bulk'
                ? 'Unloaded older sessions in the same goal or team are not included. This cannot be undone.'
                : 'This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="session-delete-cancel" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="session-delete-confirm"
              onClick={() => {
                void confirmDelete();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface PendingDelete {
  kind: 'row' | 'bulk';
  items: SessionRef[];
}
